import { useState } from "react";
import { venueHue, venueMonogram } from "../lib/format.js";
import type { VenueState } from "../lib/venues.js";

/** App-wide venue connection dot: 🟢 live (connected+funded), 🟡 connected no funds, ⚪ off. */
const DOT: Record<VenueState, string> = { live: "bg-up", nofunds: "bg-amber", off: "bg-faint" };
const DOT_TITLE: Record<VenueState, string> = {
  live: "Connected & funded — real orders work here",
  nofunds: "Connected but no balance — fund it to trade real",
  off: "Not connected — paper only",
};

/**
 * Venue identity chip. Renders the partner `logo_url` image when available (the API only hands
 * logos out on orders/opportunities), otherwise a two-letter monogram on a per-venue hue.
 * Falls back to the monogram if the image 404s. When `status` is given, overlays the app-wide
 * connection dot so "can I execute here?" reads at a glance anywhere a venue appears.
 */
export function VenueLogo({
  name,
  logoUrl,
  size = 20,
  status,
}: {
  name?: string | null;
  logoUrl?: string | null;
  size?: number;
  status?: VenueState;
}) {
  const [broken, setBroken] = useState(false);
  const px = `${size}px`;
  const radius = `${Math.round(size * 0.28)}px`;

  const inner =
    logoUrl && !broken ? (
      <img
        src={logoUrl}
        alt={name ?? "venue"}
        title={name ?? undefined}
        width={size}
        height={size}
        onError={() => setBroken(true)}
        className="block h-full w-full rounded object-contain"
        style={{ borderRadius: radius }}
      />
    ) : (
      <span
        title={name ?? undefined}
        className="flex h-full w-full items-center justify-center font-semibold text-ink"
        style={{
          borderRadius: radius,
          fontSize: `${Math.round(size * 0.42)}px`,
          background: `hsl(${venueHue(name)} 45% 22%)`,
          border: `1px solid hsl(${venueHue(name)} 45% 34%)`,
          color: `hsl(${venueHue(name)} 65% 82%)`,
        }}
      >
        {venueMonogram(name)}
      </span>
    );

  if (!status) {
    return (
      <span className="inline-block shrink-0" style={{ width: px, height: px }}>
        {inner}
      </span>
    );
  }

  const dot = Math.max(6, Math.round(size * 0.36));
  return (
    <span className="relative inline-block shrink-0" style={{ width: px, height: px }}>
      {inner}
      <span
        title={DOT_TITLE[status]}
        className={`absolute rounded-full ring-2 ring-panel-2 ${DOT[status]}`}
        style={{ width: dot, height: dot, right: -Math.round(dot * 0.28), bottom: -Math.round(dot * 0.28) }}
      />
    </span>
  );
}

/** Logo + name inline, for tables/cards. */
export function VenueChip({
  name,
  logoUrl,
  size = 18,
  status,
  className = "",
}: {
  name?: string | null;
  logoUrl?: string | null;
  size?: number;
  status?: VenueState;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <VenueLogo name={name} logoUrl={logoUrl} size={size} status={status} />
      <span className="min-w-0 break-words">{name ?? "—"}</span>
    </span>
  );
}
